import { useState } from 'react';
import {
  Plus,
  TrendingUp,
  TrendingDown,
  Pencil,
  Trash2,
  DollarSign,
  Calculator,
  ChevronDown,
  ChevronUp,
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  Percent,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useInvestments, useInvestmentDetail } from '@hooks/useInvestments';
import PageHeader from '@components/layout/PageHeader';
import Card from '@components/common/Card';
import Button from '@components/common/Button';
import Badge from '@components/common/Badge';
import Modal from '@components/common/Modal';
import Input from '@components/common/Input';
import CurrencyInput from '@components/common/CurrencyInput';
import Select from '@components/common/Select';
import Loading from '@components/common/Loading';
import EmptyState from '@components/common/EmptyState';
import PageTutorial from '@components/common/PageTutorial';
import { InvestmentForm, InvestmentSimulator } from '@components/investments';
import { formatCurrency, formatDate } from '@utils/formatters';
import { clsx } from 'clsx';

const TYPE_LABELS = {
  cdb: 'CDB',
  lci: 'LCI',
  lca: 'LCA',
  tesouro_selic: 'Tesouro Selic',
  tesouro_ipca: 'Tesouro IPCA+',
  tesouro_prefixado: 'Tesouro Prefixado',
  poupanca: 'Poupança',
  fundo: 'Fundo',
  debenture: 'Debênture',
  cri: 'CRI',
  cra: 'CRA',
  other: 'Outro',
};

// ─── MetricCard ───────────────────────────────────────────────────────────────
const MetricCard = ({ title, value, subtitle, icon, iconBg, valueColor }) => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
    <div className="flex items-start justify-between mb-3">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{title}</p>
      <div className={clsx('p-2.5 rounded-xl', iconBg)}>{icon}</div>
    </div>
    <p className={clsx('text-2xl font-bold', valueColor)}>{value}</p>
    {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
  </div>
);

const Investments = () => {
  const {
    investments,
    totalInvested,
    totalCurrentValue,
    totalEarnings,
    returnPercentage,
    isLoading,
    createInvestment,
    updateInvestment,
    deleteInvestment,
    addContribution,
    isCreating,
    isUpdating,
  } = useInvestments();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInvestment, setSelectedInvestment] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isContributionModalOpen, setIsContributionModalOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [showSimulator, setShowSimulator] = useState(false);
  const [contributionForm, setContributionForm] = useState({
    amount: '',
    type: 'contribution',
    notes: '',
  });

  // Buscar detalhes do investimento expandido
  const { data: detailData } = useInvestmentDetail(expandedId);

  const handleOpenModal = (investment = null) => {
    setSelectedInvestment(investment);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedInvestment(null);
    setIsModalOpen(false);
  };

  const handleSubmit = async (data) => {
    if (selectedInvestment) {
      await updateInvestment({ investmentId: selectedInvestment.investmentId, data });
    } else {
      await createInvestment(data);
    }
    handleCloseModal();
  };

  const handleDelete = async () => {
    if (selectedInvestment) {
      await deleteInvestment(selectedInvestment.investmentId);
      setIsDeleteModalOpen(false);
      setSelectedInvestment(null);
    }
  };

  const handleAddContribution = async () => {
    if (selectedInvestment && contributionForm.amount) {
      await addContribution({
        investmentId: selectedInvestment.investmentId,
        data: {
          amount: contributionForm.amount || 0,
          type: contributionForm.type,
          notes: contributionForm.notes,
        },
      });
      setIsContributionModalOpen(false);
      setContributionForm({ amount: '', type: 'contribution', notes: '' });
      setSelectedInvestment(null);
    }
  };

  const openContributionModal = (investment) => {
    setSelectedInvestment(investment);
    setIsContributionModalOpen(true);
  };

  if (isLoading) {
    return <Loading text="Carregando investimentos..." />;
  }

  if (showSimulator) {
    return (
      <div>
        <PageHeader
          title="Simulador de Renda Fixa"
          subtitle="Calcule o rendimento dos seus investimentos"
          action={
            <Button variant="secondary" onClick={() => setShowSimulator(false)}>
              Voltar
            </Button>
          }
        />
        <InvestmentSimulator />
      </div>
    );
  }

  const earningsPositive = totalEarnings >= 0;
  const returnPositive = returnPercentage >= 0;

  return (
    <div className="space-y-6 pb-8">
      <PageHeader
        title="Renda Fixa"
        subtitle="Controle seus investimentos em renda fixa"
        action={
          <div className="flex gap-2">
            <Button
              variant="secondary"
              leftIcon={<Calculator size={18} />}
              onClick={() => setShowSimulator(true)}
            >
              Simulador
            </Button>
            <Button leftIcon={<Plus size={18} />} onClick={() => handleOpenModal()}>
              Novo Investimento
            </Button>
          </div>
        }
      />

      <PageTutorial
        pageId="investments"
        title="Seus investimentos"
        steps={[
          'Cadastre investimentos de renda fixa como CDB, LCI, Tesouro Direto, etc',
          'Registre aportes e dividendos para acompanhar a evolucao',
          'A projecao de rendimento estima o crescimento baseado nas taxas cadastradas',
        ]}
      />

      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-primary-900/80 to-slate-900 text-white shadow-2xl p-6">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-primary-500/10 blur-2xl" />
        <div className="absolute -bottom-8 -left-8 w-36 h-36 rounded-full bg-primary-400/10 blur-2xl" />
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-white/50 text-sm font-medium mb-1">Carteira de Renda Fixa</p>
            <p className={clsx(
              'text-4xl font-bold tracking-tight',
              earningsPositive ? 'text-white' : 'text-red-400'
            )}>
              {formatCurrency(totalCurrentValue)}
            </p>
            <div className={clsx(
              'flex items-center gap-2 mt-2',
              earningsPositive ? 'text-emerald-400' : 'text-red-400'
            )}>
              {earningsPositive ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
              <span className="text-sm font-semibold">
                {earningsPositive ? '+' : ''}{formatCurrency(totalEarnings)} em rendimentos
              </span>
            </div>
          </div>
          <div className="flex gap-6">
            <div className="text-center">
              <p className="text-white/40 text-xs mb-1">Investido</p>
              <p className="text-white font-bold text-lg">{formatCurrency(totalInvested)}</p>
            </div>
            <div className="w-px bg-white/10" />
            <div className="text-center">
              <p className="text-white/40 text-xs mb-1">Rentabilidade</p>
              <p className={clsx(
                'font-bold text-lg',
                returnPositive ? 'text-emerald-400' : 'text-red-400'
              )}>
                {returnPositive ? '+' : ''}{returnPercentage.toFixed(2)}%
              </p>
            </div>
            <div className="w-px bg-white/10" />
            <div className="text-center">
              <p className="text-white/40 text-xs mb-1">Ativos</p>
              <p className="text-white font-bold text-lg">{investments.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Investido"
          value={formatCurrency(totalInvested)}
          subtitle={`${investments.length} ativo${investments.length !== 1 ? 's' : ''}`}
          icon={<Wallet size={18} className="text-primary-600" />}
          iconBg="bg-primary-100 dark:bg-primary-900/30"
          valueColor="text-gray-900 dark:text-white"
        />
        <MetricCard
          title="Valor Atual"
          value={formatCurrency(totalCurrentValue)}
          icon={<TrendingUp size={18} className="text-emerald-600" />}
          iconBg="bg-emerald-100 dark:bg-emerald-900/30"
          valueColor="text-emerald-600"
        />
        <MetricCard
          title="Rendimento Total"
          value={`${earningsPositive ? '+' : ''}${formatCurrency(totalEarnings)}`}
          icon={
            earningsPositive
              ? <ArrowUpRight size={18} className="text-emerald-600" />
              : <ArrowDownRight size={18} className="text-red-600" />
          }
          iconBg={earningsPositive ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-red-100 dark:bg-red-900/30'}
          valueColor={earningsPositive ? 'text-emerald-600' : 'text-red-600'}
        />
        <MetricCard
          title="Rentabilidade"
          value={`${returnPositive ? '+' : ''}${returnPercentage.toFixed(2)}%`}
          icon={<Percent size={18} className={returnPositive ? 'text-emerald-600' : 'text-red-600'} />}
          iconBg={returnPositive ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-red-100 dark:bg-red-900/30'}
          valueColor={returnPositive ? 'text-emerald-600' : 'text-red-600'}
        />
      </div>

      {/* Investments List */}
      {investments.length === 0 ? (
        <EmptyState
          icon={<TrendingUp size={32} />}
          title="Nenhum investimento cadastrado"
          description="Comece a controlar seus investimentos em renda fixa."
          action={() => handleOpenModal()}
          actionLabel="Adicionar Investimento"
        />
      ) : (
        <div className="space-y-4">
          {investments.map((investment) => {
            const isExpanded = expandedId === investment.investmentId;
            const detail = isExpanded ? detailData : null;

            return (
              <Card key={investment.investmentId} padding="none" className="overflow-hidden">
                {/* Header */}
                <div
                  className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                  onClick={() => setExpandedId(isExpanded ? null : investment.investmentId)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                        style={{ backgroundColor: `${investment.color}20` }}
                      >
                        {investment.icon}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-gray-900 dark:text-white">
                            {investment.name}
                          </p>
                          <Badge variant="primary" size="sm">
                            {TYPE_LABELS[investment.type] || investment.type}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {investment.institution || 'Sem instituição'} •
                          {investment.interestType === 'cdi_percentage'
                            ? ` ${investment.cdiPercentage}% do CDI`
                            : ` ${investment.interestRate}% ${investment.interestType === 'monthly' ? 'a.m.' : 'a.a.'}`
                          }
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-sm text-gray-500 dark:text-gray-400">Valor Atual</p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">
                          {formatCurrency(investment.projectedCurrentValue || investment.currentAmount)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500 dark:text-gray-400">Rendimento</p>
                        <p className={clsx(
                          'text-lg font-bold flex items-center gap-1',
                          investment.totalReturn >= 0 ? 'text-emerald-600' : 'text-red-600'
                        )}>
                          {investment.totalReturn >= 0 ? (
                            <ArrowUpRight size={16} />
                          ) : (
                            <ArrowDownRight size={16} />
                          )}
                          {formatCurrency(Math.abs(investment.totalReturn || 0))}
                        </p>
                      </div>
                      {investment.goalAmount && (
                        <div className="text-right">
                          <p className="text-sm text-gray-500 dark:text-gray-400">Meta</p>
                          <p className="text-sm font-medium">
                            {investment.goalProgress?.toFixed(0)}%
                          </p>
                        </div>
                      )}
                      {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </div>
                  </div>

                  {/* Progress bar para meta */}
                  {investment.goalAmount && (
                    <div className="mt-4">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>{investment.goalDescription || 'Meta'}</span>
                        <span>{formatCurrency(investment.goalAmount)}</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-primary-600 h-2 rounded-full transition-all"
                          style={{ width: `${Math.min(100, investment.goalProgress || 0)}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="border-t border-gray-200 dark:border-gray-700">
                    <div className="p-4 bg-gray-50 dark:bg-gray-800/50">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          Detalhes do Investimento
                        </h4>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            leftIcon={<DollarSign size={14} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              openContributionModal(investment);
                            }}
                          >
                            Movimentar
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            leftIcon={<Pencil size={14} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenModal(investment);
                            }}
                          >
                            Editar
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            leftIcon={<Trash2 size={14} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedInvestment(investment);
                              setIsDeleteModalOpen(true);
                            }}
                          >
                            Excluir
                          </Button>
                        </div>
                      </div>

                      {/* Info Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div>
                          <p className="text-xs text-gray-500">Valor Inicial</p>
                          <p className="font-semibold">{formatCurrency(investment.initialAmount)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Aporte Mensal</p>
                          <p className="font-semibold">{formatCurrency(investment.monthlyContribution || 0)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Data de Início</p>
                          <p className="font-semibold">{formatDate(investment.startDate)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Vencimento</p>
                          <p className="font-semibold">
                            {investment.maturityDate ? formatDate(investment.maturityDate) : 'Sem vencimento'}
                          </p>
                        </div>
                      </div>

                      {/* Projeção Futura */}
                      {detail?.futureProjection?.monthly && (
                        <div className="mb-6">
                          <h5 className="font-medium text-gray-900 dark:text-white mb-3">
                            Projeção (próximos 12 meses)
                          </h5>
                          <ResponsiveContainer width="100%" height={200}>
                            <LineChart data={detail.futureProjection.monthly}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                              <XAxis dataKey="monthLabel" stroke="#9ca3af" fontSize={10} />
                              <YAxis
                                tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                                stroke="#9ca3af"
                                fontSize={10}
                              />
                              <Tooltip formatter={(value) => formatCurrency(value)} />
                              <Line
                                type="monotone"
                                dataKey="balance"
                                name="Saldo Projetado"
                                stroke="#22c55e"
                                strokeWidth={2}
                                dot={{ r: 3 }}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                          <div className="grid grid-cols-3 gap-4 mt-4 text-center">
                            <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                              <p className="text-xs text-gray-500">Valor Atual</p>
                              <p className="font-bold text-primary-600">
                                {formatCurrency(detail.futureProjection.currentValue)}
                              </p>
                            </div>
                            <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                              <p className="text-xs text-gray-500">Projeção 12 meses</p>
                              <p className="font-bold text-emerald-600">
                                {formatCurrency(detail.futureProjection.projectedValue)}
                              </p>
                            </div>
                            <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                              <p className="text-xs text-gray-500">Rendimento Previsto</p>
                              <p className="font-bold text-emerald-600">
                                +{formatCurrency(detail.futureProjection.totalEarnings)}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Histórico */}
                      {investment.history && investment.history.length > 0 && (
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-white mb-3">
                            Histórico de Movimentações
                          </h5>
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {[...investment.history].reverse().slice(0, 10).map((entry) => (
                              <div
                                key={entry.id}
                                className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded-lg"
                              >
                                <div className="flex items-center gap-3">
                                  <div className={clsx(
                                    'w-8 h-8 rounded-full flex items-center justify-center text-xs',
                                    entry.type === 'initial' ? 'bg-blue-100 text-blue-600' :
                                    entry.type === 'contribution' ? 'bg-emerald-100 text-emerald-600' :
                                    entry.type === 'withdrawal' ? 'bg-red-100 text-red-600' :
                                    'bg-purple-100 text-purple-600'
                                  )}>
                                    {entry.type === 'initial' ? '🚀' :
                                     entry.type === 'contribution' ? '+' :
                                     entry.type === 'withdrawal' ? '-' : '%'}
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium">{entry.description}</p>
                                    <p className="text-xs text-gray-500">{formatDate(entry.date)}</p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className={clsx(
                                    'font-semibold',
                                    entry.type === 'withdrawal' ? 'text-red-600' : 'text-emerald-600'
                                  )}>
                                    {entry.type === 'withdrawal' ? '-' : '+'}{formatCurrency(entry.amount)}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Saldo: {formatCurrency(entry.balance)}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Form Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={selectedInvestment ? 'Editar Investimento' : 'Novo Investimento'}
        size="lg"
      >
        <InvestmentForm
          investment={selectedInvestment}
          onSubmit={handleSubmit}
          onCancel={handleCloseModal}
          isLoading={isCreating || isUpdating}
        />
      </Modal>

      {/* Contribution Modal */}
      <Modal
        isOpen={isContributionModalOpen}
        onClose={() => setIsContributionModalOpen(false)}
        title="Movimentação"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsContributionModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddContribution}>
              Confirmar
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Select
            label="Tipo"
            options={[
              { value: 'contribution', label: '💰 Aporte' },
              { value: 'withdrawal', label: '💸 Resgate' },
            ]}
            value={contributionForm.type}
            onChange={(e) => setContributionForm({ ...contributionForm, type: e.target.value })}
          />
          <CurrencyInput
            label="Valor"
            value={contributionForm.amount}
            onValueChange={(values) => setContributionForm({ ...contributionForm, amount: values.floatValue ?? '' })}
          />
          <Input
            label="Observação (opcional)"
            placeholder="Ex: Aporte mensal janeiro"
            value={contributionForm.notes}
            onChange={(e) => setContributionForm({ ...contributionForm, notes: e.target.value })}
          />
        </div>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Excluir Investimento"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Excluir
            </Button>
          </>
        }
      >
        <p className="text-gray-600 dark:text-gray-400">
          Tem certeza que deseja excluir o investimento <strong>{selectedInvestment?.name}</strong>?
          Todo o histórico será perdido.
        </p>
      </Modal>
    </div>
  );
};

export default Investments;
